import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  BarChart3, 
  Users, 
  Settings, 
  Shield, 
  MessageSquare,
  Webhook,
  UserCheck,
  X,
  Brain,
  Activity,
  BookOpen,
  Play,
  TrendingUp,
  Cog,
  HelpCircle,
  Zap,
  Code,
  Download
} from 'lucide-react';

const ConsoleSidebar = ({ isOpen, onClose, currentSection, currentPath }) => {
  const navigate = useNavigate();
  
  const navigation = [
    {
      name: 'Dashboard',
      path: '/console',
      icon: Home,
      current: currentPath === '/console',
      description: 'Overview of your AI agents and system status'
    },
    {
      name: '🚀 Quick Start',
      icon: Play,
      current: false,
      children: [
        { name: 'Drop-in SDK Integration', path: '/console/documentation', icon: Code, current: currentPath === '/console/documentation', description: 'Just import and wrap your agent - everything else is automatic!' },
        { name: 'Create Your First Agent', path: '/console/admin/agents', icon: UserCheck, current: currentPath === '/console/admin/agents', description: 'Register and configure your first AI agent' },
        { name: 'Set Up API Keys', path: '/console/admin/api-keys', icon: Shield, current: currentPath === '/console/admin/api-keys', description: 'Configure API keys for agent access' },
        { name: 'Download SDK', path: '/console/documentation', icon: Download, current: currentPath === '/console/documentation', description: 'Get the drop-in SDK for automatic monitoring' },
      ]
    },
    {
      name: '📊 Monitor & Optimize',
      icon: TrendingUp,
      current: currentPath.startsWith('/console/analytics'),
      children: [
        { name: 'Agent Performance', path: '/console/analytics/agents', icon: BarChart3, current: currentPath === '/console/analytics/agents', description: 'Track agent performance and metrics' },
        { name: 'Feedback Analysis', path: '/console/analytics/feedback', icon: MessageSquare, current: currentPath === '/console/analytics/feedback', description: 'Review and analyze agent feedback' },
        { name: 'Customer Insights', path: '/console/analytics/customers', icon: Users, current: currentPath === '/console/analytics/customers', description: 'Monitor customer interactions and usage' },
      ]
    },
    {
      name: '⚙️ Advanced Management',
      icon: Cog,
      current: currentPath.startsWith('/console/admin') && !currentPath.startsWith('/console/admin/agents') && !currentPath.startsWith('/console/admin/api-keys'),
      children: [
        { name: 'Agent Self-Healing', path: '/console/admin/agents', icon: Brain, current: currentPath === '/console/admin/agents', description: 'Automated agent optimization and improvement' },
        { name: 'Customer Management', path: '/console/admin/customers', icon: Users, current: currentPath === '/console/admin/customers', description: 'Manage customer accounts and access' },
        { name: 'Webhook Configuration', path: '/console/admin/webhooks', icon: Webhook, current: currentPath === '/console/admin/webhooks', description: 'Set up real-time notifications and integrations' },
      ]
    },
    {
      name: '🔧 Settings & Help',
      icon: HelpCircle,
      current: currentPath === '/console/settings',
      children: [
        { name: 'Account Settings', path: '/console/settings', icon: Settings, current: currentPath === '/console/settings', description: 'Manage your account and preferences' },
        { name: 'Platform Documentation', path: '/console/documentation', icon: BookOpen, current: currentPath === '/console/documentation', description: 'Comprehensive guides and tutorials' },
      ]
    }
  ];

  const handleNavigation = (path, e) => {
    e.preventDefault();
    navigate(path);
    onClose();
  };

  return (
    <div className={`
      fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:flex-shrink-0
      ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">E</span>
          </div>
          <span className="ml-3 text-lg font-semibold text-gray-900">Enable.AI Console</span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigation.map((item) => (
          <div key={item.name}>
            {item.children ? (
              <div>
                <div className="flex items-center px-3 py-2 text-sm font-medium text-gray-600">
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </div>
                <div className="ml-6 space-y-1">
                  {item.children.map((child) => (
                    <button
                      key={child.name}
                      onClick={(e) => handleNavigation(child.path, e)}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                        child.current
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      title={child.description}
                    >
                      <child.icon className="mr-3 h-4 w-4" />
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={(e) => handleNavigation(item.path, e)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  item.current
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={item.description}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.name}
              </button>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4">
        <div className="text-xs text-gray-500 text-center">
          Enable.AI Console v1.0
        </div>
      </div>
    </div>
  );
};

export default ConsoleSidebar; 